import { useEffect } from "react";

/**
 * Comprehensive SEO helper:
 *  - title + meta description + canonical
 *  - Open Graph (article|website) + Twitter Card
 *  - Robots index/noindex
 *  - JSON-LD schema (any number of objects)
 */
export default function Seo({
  title,
  description,
  canonical,
  image,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  keywords,
  noindex = false,
  schema, // single object OR array of objects
}) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);
    setMeta("keywords", keywords);
    setLink("canonical", canonical);

    setMeta("og:type", type, "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:site_name", "Puppl", "property");
    if (canonical) setMeta("og:url", canonical, "property");
    if (image) setMeta("og:image", image, "property");
    if (publishedTime) setMeta("article:published_time", publishedTime, "property");
    if (modifiedTime) setMeta("article:modified_time", modifiedTime, "property");
    if (author) setMeta("article:author", author, "property");

    setMeta("twitter:card", image ? "summary_large_image" : "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (image) setMeta("twitter:image", image);

    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");

    // JSON-LD
    cleanupSchema();
    const arr = Array.isArray(schema) ? schema : (schema ? [schema] : []);
    arr.filter(Boolean).forEach((obj, i) => {
      const tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.setAttribute("data-seo-schema", String(i));
      try { tag.text = JSON.stringify(obj); } catch { return; }
      document.head.appendChild(tag);
    });

    return cleanupSchema;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, type, publishedTime, modifiedTime, author, keywords, noindex, JSON.stringify(schema || null)]);

  return null;
}

function setMeta(name, content, keyAttr = "name") {
  if (content == null || content === "") return removeMeta(name, keyAttr);
  let el = document.head.querySelector(`meta[${keyAttr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyAttr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name, keyAttr = "name") {
  const el = document.head.querySelector(`meta[${keyAttr}="${name}"]`);
  if (el) el.remove();
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function cleanupSchema() {
  document.head.querySelectorAll("script[data-seo-schema]").forEach((n) => n.remove());
}

/**
 * Default org-wide JSON-LD blocks. Drop into Landing/About so Google has them.
 */
export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://puppl.app/#org",
  "name": "Puppl",
  "alternateName": "Paws Claws Tails — Sunshine Coast Puppy Training",
  "url": "https://puppl.app",
  "logo": "https://puppl.app/logo.png",
  "image": "https://puppl.app/logo.png",
  "email": "support@puppl.app",
  "areaServed": { "@type": "Place", "name": "Sunshine Coast, Queensland" },
  "address": { "@type": "PostalAddress", "addressLocality": "Sunshine Coast", "addressRegion": "QLD", "addressCountry": "AU" },
  "priceRange": "$$",
  "founder": { "@type": "Person", "name": "the Puppl team" },
  "knowsAbout": ["Puppy training", "Loose-lead walking", "Recall training", "Puppy socialisation", "Fear-free dog training", "Group puppy classes"]
};

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": it.url,
    })),
  };
}

export function articleSchema({ title, description, canonical, image, publishedTime, modifiedTime, author = "the Puppl team" }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "mainEntityOfPage": canonical,
    "image": image || "https://puppl.app/logo.png",
    "author": { "@type": "Person", "name": author },
    "publisher": {
      "@type": "Organization",
      "name": "Puppl",
      "logo": { "@type": "ImageObject", "url": "https://puppl.app/logo.png" },
    },
    "datePublished": publishedTime,
    "dateModified": modifiedTime || publishedTime,
  };
}
